/**
 * Clears Segment's persisted analytics identity.
 *
 * Segment persists it on the shared parent domain (`.autodesk.com`) for 365 days, so
 * without an explicit clear it survives sign-out and is inherited by the next user
 * of the browser (WPX-3673). Clearing cookies directly is more reliable than
 * `analytics.reset()` alone — before Analytics.js loads that call just queues.
 */

/** Segment's persisted user identity cookie. */
export const SEGMENT_USER_ID_COOKIE = 'ajs_user_id';

/** Segment's persisted anonymous (device) identity cookie. */
export const SEGMENT_ANONYMOUS_ID_COOKIE = 'ajs_anonymous_id';

/**
 * Everything Segment's own `logout()` drops, beyond the two identity cookies.
 *
 * Kept as one list so the set cannot drift from what `Entity.prototype.logout()`
 * clears. Traits matter as much as the id: `identify(id, traits)` merges into stored
 * traits when the stored id is `null`, so traits left behind after an id-only clear
 * are folded into the *next* user's identify — the departing user's email attached to
 * a correct-looking id, which is harder to spot than WPX-3673 itself.
 *
 * Each is cleared from both stores. Which one Segment uses depends on version and on
 * `localStorageFallbackDisabled`, and clearing a key that was never written costs
 * nothing.
 */
const SEGMENT_RESIDUAL_KEYS = [
  'ajs_user_traits',
  'ajs_group_id',
  'ajs_group_properties',
];

/**
 * Every domain scope a Segment cookie may have been written to — the host itself
 * plus each dot-prefixed parent, since a cookie can only be deleted from the scope
 * that created it and Segment writes to the shared parent domain.
 *
 * @param hostname The current `window.location.hostname`.
 * @returns Domain values to attempt, `undefined` meaning a host-only cookie.
 */
export const getCookieDomainScopes = (
  hostname: string,
): Array<string | undefined> => {
  const parts = hostname.split('.');
  const scopes: Array<string | undefined> = [undefined];

  for (let i = 0; i < parts.length - 1; i += 1) {
    scopes.push(`.${parts.slice(i).join('.')}`);
  }

  return scopes;
};

/**
 * Expires a cookie across every plausible domain scope.
 *
 * @param name Name of the cookie to expire.
 * @returns Nothing.
 */
const expireCookie = (name: string): void => {
  if (typeof document === 'undefined') return;

  getCookieDomainScopes(window.location.hostname).forEach((domain) => {
    const domainPart = domain ? `; domain=${domain}` : '';
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`;
  });
};

/**
 * Clears both the user and anonymous analytics identities.
 *
 * Use this on an explicit sign-out. The anonymous id is dropped as well so the next
 * person to use the browser starts a genuinely new device identity rather than
 * inheriting the previous user's, which would otherwise stitch the two together.
 *
 * @returns Nothing.
 */
export const clearAnalyticsIdentity = (): void => {
  expireCookie(SEGMENT_USER_ID_COOKIE);
  expireCookie(SEGMENT_ANONYMOUS_ID_COOKIE);

  SEGMENT_RESIDUAL_KEYS.forEach((key) => {
    expireCookie(key);
    try {
      localStorage.removeItem(key);
    } catch {
      // The `localStorage` getter itself raises SecurityError where storage is
      // blocked or partitioned. Guarded per key so one failure cannot skip the rest.
    }
  });
};
